import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, FormControl, FormLabel,
    Input, List, ListItem, Option, Select, Sheet, Stack, Textarea, Typography,
} from '@mui/joy';
import { useNavigate, useParams } from 'react-router';

import { useAdmin } from '../../../AdminContext';
import {
    APIAdminAdsApplicationDetail, APIAdminAdsApplicationUpdate,
    APIAdminAdsApplicationSubmit, APIAdminAdsApplicationCreativeCreate, adminAdsError,
} from '../../../api_admin_ads';
import AdStatusChip from '../../../components/Ads/AdStatusChip';
import { FORMAT_LABELS, PRICING_LABELS, formatMoney } from '../../../components/Ads/adsConstants';

const EDITABLE = ['draft', 'rejected', 'changes_requested'];
const OVERSELL_COLOR = { ok: 'success', tight: 'warning', oversold: 'danger', unknown: 'neutral' };

export default function AdminAdsApplicationDetailPage() {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const { adminToken: token, has } = useAdmin();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitErrors, setSubmitErrors] = useState([]);
    const [busy, setBusy] = useState(false);

    // форма нового объявления
    const [creative, setCreative] = useState({
        format: 'deck_card', title: '', text: '', cta_text: '',
        brand_name: '', landing_url: '', image: null,
    });

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdminAdsApplicationDetail(token, campaignId);
            setData(res);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить заявку'));
        } finally {
            setLoading(false);
        }
    }, [token, campaignId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>;
    if (error) return <Alert color="danger">{error}</Alert>;
    if (!data?.campaign) return <Alert color="neutral">Заявка не найдена</Alert>;

    const c = data.campaign;
    const group = (c.groups || [])[0];
    const editable = has('ads.manage') && EDITABLE.includes(c.status);
    const oversell = data.oversell || {};

    const addCreative = async () => {
        if (!group) { setError('У заявки нет группы объявлений'); return; }
        if (!creative.title.trim() || !creative.landing_url.trim() || !creative.brand_name.trim()) {
            setError('Заполните заголовок, ссылку и название рекламодателя'); return;
        }
        setBusy(true);
        setError(null);
        try {
            const fd = new FormData();
            fd.append('format', creative.format);
            fd.append('title', creative.title.trim());
            fd.append('text', creative.text.trim());
            fd.append('cta_text', creative.cta_text.trim());
            fd.append('brand_name', creative.brand_name.trim());
            fd.append('landing_url', creative.landing_url.trim());
            if (creative.image) fd.append('image', creative.image);
            await APIAdminAdsApplicationCreativeCreate(token, group.id, fd);
            setCreative({ format: 'deck_card', title: '', text: '', cta_text: '',
                brand_name: '', landing_url: '', image: null });
            await load();
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось добавить объявление'));
        } finally {
            setBusy(false);
        }
    };

    const submit = async () => {
        setBusy(true);
        setSubmitErrors([]);
        setError(null);
        try {
            const res = await APIAdminAdsApplicationSubmit(token, campaignId);
            if (res?.success) await load();
        } catch (e) {
            const list = e?.response?.data?.errors;
            if (Array.isArray(list) && list.length) setSubmitErrors(list);
            else setError(adminAdsError(e, 'Не удалось отправить заявку'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 900 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography level="h4">Заявка #{c.id}: {c.name}</Typography>
                <AdStatusChip status={c.status} />
            </Stack>
            <Button size="sm" variant="plain" color="neutral" sx={{ mb: 2 }}
                    onClick={() => navigate('/admin/ads/applications')}>← К списку</Button>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Рекламодатель и параметры */}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
                    <Box>
                        <Typography level="body-xs" color="neutral">Рекламодатель</Typography>
                        <Typography level="body-sm">
                            {data.advertiser ? data.advertiser.company_name : 'Платформа'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography level="body-xs" color="neutral">Оплата</Typography>
                        <Typography level="body-sm">{PRICING_LABELS[c.pricing_model]}</Typography>
                    </Box>
                    <Box>
                        <Typography level="body-xs" color="neutral">Бюджет/сут</Typography>
                        <Typography level="body-sm">{formatMoney(c.daily_budget)} ₽</Typography>
                    </Box>
                    <Box>
                        <Typography level="body-xs" color="neutral">Охват</Typography>
                        <Typography level="body-sm">{data.geo_scope_display || c.geo_scope}</Typography>
                    </Box>
                </Stack>

                {oversell.status && (
                    <Alert color={OVERSELL_COLOR[oversell.status] || 'neutral'} sx={{ mt: 2 }}>
                        Ёмкость территории: <b>{oversell.status}</b>
                        {oversell.message ? ` — ${oversell.message}` : ''}
                    </Alert>
                )}
            </Sheet>

            {/* Объявления */}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                <Typography level="title-md" sx={{ mb: 1 }}>Объявления</Typography>
                <List sx={{ mb: editable ? 2 : 0 }}>
                    {(group?.creatives || []).map((cr) => (
                        <ListItem key={cr.id}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip size="sm" variant="soft">{FORMAT_LABELS[cr.format] || cr.format}</Chip>
                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>{cr.title}</Typography>
                                <Chip size="sm" color={cr.image ? 'success' : 'warning'} variant="soft">
                                    {cr.image ? 'с картинкой' : 'без картинки'}
                                </Chip>
                            </Stack>
                        </ListItem>
                    ))}
                    {(!group?.creatives || group.creatives.length === 0) && (
                        <Typography level="body-sm" color="neutral">Пока нет объявлений</Typography>
                    )}
                </List>

                {editable && (
                    <>
                        <Divider sx={{ mb: 2 }} />
                        <Typography level="title-sm" sx={{ mb: 1.5 }}>Добавить объявление</Typography>
                        <Stack spacing={1.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Формат</FormLabel>
                                    <Select value={creative.format}
                                            onChange={(_, v) => setCreative({ ...creative, format: v })}>
                                        {Object.entries(FORMAT_LABELS).map(([v, l]) => (
                                            <Option key={v} value={v}>{l}</Option>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Название рекламодателя (по закону)</FormLabel>
                                    <Input value={creative.brand_name}
                                           onChange={(e) => setCreative({ ...creative, brand_name: e.target.value })} />
                                </FormControl>
                            </Stack>
                            <FormControl>
                                <FormLabel>Заголовок</FormLabel>
                                <Input value={creative.title}
                                       onChange={(e) => setCreative({ ...creative, title: e.target.value })} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Текст</FormLabel>
                                <Textarea minRows={2} value={creative.text}
                                          onChange={(e) => setCreative({ ...creative, text: e.target.value })} />
                            </FormControl>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Текст кнопки</FormLabel>
                                    <Input value={creative.cta_text}
                                           onChange={(e) => setCreative({ ...creative, cta_text: e.target.value })} />
                                </FormControl>
                                <FormControl sx={{ flex: 2 }}>
                                    <FormLabel>Целевая ссылка</FormLabel>
                                    <Input placeholder="https://…" value={creative.landing_url}
                                           onChange={(e) => setCreative({ ...creative, landing_url: e.target.value })} />
                                </FormControl>
                            </Stack>
                            <FormControl>
                                <FormLabel>Изображение</FormLabel>
                                <input type="file" accept="image/*"
                                       onChange={(e) => setCreative({ ...creative, image: e.target.files?.[0] || null })} />
                            </FormControl>
                            <Box>
                                <Button size="sm" loading={busy} onClick={addCreative}>Добавить объявление</Button>
                            </Box>
                        </Stack>
                    </>
                )}
            </Sheet>

            {/* Отправка на модерацию */}
            {editable && (
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                    {submitErrors.length > 0 && (
                        <Alert color="danger" sx={{ mb: 2 }}>
                            <Box>
                                <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Заявку нельзя отправить:
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {submitErrors.map((msg, i) => <li key={i}>{msg}</li>)}
                                </ul>
                            </Box>
                        </Alert>
                    )}
                    <Button loading={busy} onClick={submit}>Отправить на модерацию</Button>
                </Sheet>
            )}
        </Box>
    );
}